// src/pages/SitesPage.jsx
//
// Module 04 — Site / Property Management
// Lists sites the current user can see (owner-scoped for non-SuperAdmin),
// with create + delete + navigate-to-detail actions.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listSites, createSite, deleteSite } from '../api/sitesApi';
import RequirePermission from '../auth/RequirePermission';

export default function SitesPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSites();
      setSites(data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="shell">
      <header className="page-header">
        <div>
          <h1>Sites</h1>
          <p className="muted">Websites and properties registered to your account.</p>
        </div>
        <RequirePermission code="sites.create">
          <button className="primary" onClick={() => setCreateOpen(true)}>
            + New site
          </button>
        </RequirePermission>
      </header>

      {loading && <div className="card">Loading…</div>}
      {error && !loading && <div className="card toast danger">{error}</div>}

      {!loading && !error && sites.length === 0 && (
        <div className="card empty-state">
          <p>No sites yet.</p>
          <RequirePermission code="sites.create">
            <button className="primary" onClick={() => setCreateOpen(true)}>
              Register your first site
            </button>
          </RequirePermission>
        </div>
      )}

      {!loading && !error && sites.length > 0 && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Public key</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Created</th>
                <th style={{ width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <SiteRow key={s.id} site={s} onDelete={() => setDeleteTarget(s)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <CreateSiteModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      )}

      {deleteTarget && (
        <DeleteSiteModal
          site={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

// ── Table row ──────────────────────────────────────────────────────────

function SiteRow({ site, onDelete }) {
  return (
    <tr>
      <td>{site.name}</td>
      <td>
        <a href={`https://${site.domain}`} target="_blank" rel="noopener noreferrer">
          {site.domain}
        </a>
      </td>
      <td>
        {site.activePublicKey ? (
          <KeyPreview value={site.activePublicKey} />
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        <StatusBadge status={site.status} />
      </td>
      <td>{site.ownerEmail || '—'}</td>
      <td>{formatDate(site.createdAt)}</td>
      <td className="actions">
        <Link className="ghost" to={`/sites/${site.id}`}>
          View
        </Link>
        <RequirePermission code="sites.delete">
          <button className="ghost danger" onClick={onDelete}>
            Delete
          </button>
        </RequirePermission>
      </td>
    </tr>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────────

function KeyPreview({ value }) {
  const [copied, setCopied] = useState(false);
  const short =
    value.length > 16 ? `${value.slice(0, 12)}…${value.slice(-4)}` : value;

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  }

  return (
    <span className="key-preview" onClick={copy} title="Click to copy full key">
      <code>{short}</code>
      <span className="muted">{copied ? '✓ copied' : 'copy'}</span>
    </span>
  );
}

function StatusBadge({ status }) {
  const cls =
    (status || '').toLowerCase() === 'disabled' ? 'badge danger' : 'badge';
  return <span className={cls}>{status}</span>;
}

// ── Create modal ──────────────────────────────────────────────────────

function CreateSiteModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await createSite({ name: name.trim(), domain: domain.trim() });
      onCreated();
    } catch (e2) {
      setErr(extractError(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card" onClick={(e) => e.stopPropagation()}>
        <h2>New site</h2>
        <form onSubmit={submit}>
          <label>
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={128}
              required
              autoFocus
              disabled={busy}
              placeholder="My production site"
            />
          </label>
          <label>
            Domain
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              maxLength={253}
              required
              disabled={busy}
              placeholder="example.com"
            />
            <small className="muted">
              Don't include http:// or paths — they'll be stripped automatically.
            </small>
          </label>
          {err && <div className="toast danger">{err}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create site'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete confirm ────────────────────────────────────────────────────

function DeleteSiteModal({ site, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteSite(site.id);
      onDeleted();
    } catch (e) {
      setErr(extractError(e));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card" onClick={(e) => e.stopPropagation()}>
        <h2>Delete site?</h2>
        <p>
          This will permanently delete <strong>{site.name}</strong>{' '}
          (<code>{site.domain}</code>) and revoke all of its tracking keys.
          This cannot be undone.
        </p>
        {err && <div className="toast danger">{err}</div>}
        <div className="actions">
          <button className="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="danger" onClick={confirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete site'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function extractError(err) {
  const data = err?.response?.data;
  if (data?.message) return data.message;
  if (typeof err?.message === 'string') return err.message;
  return 'Something went wrong. Please try again.';
}